import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { authAPI } from '../services/api';
import { decryptValue, encryptData } from '../utils/encryption';
import toast from 'react-hot-toast';
import { Eye, EyeOff, Lock, CheckCircle, XCircle } from 'lucide-react';

interface ResetPasswordProps {}

const ResetPassword: React.FC<ResetPasswordProps> = () => {
  const location = useLocation();
  console.log('ResetPassword - location.pathname:', location.pathname);
  
  // Extract token from pathname (handle both /forget-password/ and /forgot-password/)
  let token = location.pathname.replace(/^\/forget-password\//, '');
  if (token === location.pathname) {
    token = location.pathname.replace(/^\/forgot-password\//, '');
  }
  console.log('ResetPassword - extracted token:', token);
  const [isLoading, setIsLoading] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [email, setEmail] = useState('');
  const [passwordResetStatus, setPasswordResetStatus] = useState<boolean | null>(null);
  const [showModal, setShowModal] = useState(false);
  
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm();

  const password = watch('password');

  useEffect(() => {
    const checkPasswordResetStatus = async () => {
      console.log("token from URL", token);
      if (!token || token.trim() === '') {
        // If no token, redirect to forgot password page
        navigate('/forgot-password');
        return;
      }

      try {
        setIsChecking(true);
        setErrorMessage('');
        
        // Step 1: URL decode the token (in case it was URL encoded)
        const decodedToken = decodeURIComponent(token);
        console.log("decodedToken", decodedToken);
        
        // Step 2: Decode base64 token from URL
        let urlSafeEncryptedToken: string;
        try {
          urlSafeEncryptedToken = atob(decodedToken);
          console.log("urlSafeEncryptedToken after base64 decode", urlSafeEncryptedToken);
        } catch (error) {
          console.error('Base64 decode error:', error);
          setErrorMessage('Invalid reset link format. Please request a new password reset.');
          setIsChecking(false);
          return;
        }

        // Step 2: Reverse URL-safe encoding (replace - with +, _ with /, and add padding if needed)
        let encryptedToken = urlSafeEncryptedToken
          .replace(/-/g, '+')
          .replace(/_/g, '/');
        
        // Add padding if needed (base64 padding is =)
        const padLength = (4 - (encryptedToken.length % 4)) % 4;
        if (padLength > 0) {
          encryptedToken += '='.repeat(padLength);
        }

        // Step 3: Decrypt the token to get the email
        let decryptedEmail: string;
        try {
          const decryptedValue = decryptValue(encryptedToken);
          console.log('ResetPassword - decryptedValue (raw):', decryptedValue);
          
          // The backend might JSON stringify the email, so try to parse it
          // If it's a JSON string (starts and ends with quotes), parse it
          if (decryptedValue.startsWith('"') && decryptedValue.endsWith('"')) {
            decryptedEmail = JSON.parse(decryptedValue);
          } else {
            decryptedEmail = decryptedValue;
          }
          
          console.log('ResetPassword - decryptedEmail (cleaned):', decryptedEmail);
        } catch (error) {
          console.error('Decryption error:', error);
          setErrorMessage('Invalid reset link. Please request a new password reset.');
          setIsChecking(false);
          return;
        }

        if (!decryptedEmail || !decryptedEmail.trim()) {
          setErrorMessage('Invalid reset link. Please request a new password reset.');
          setIsChecking(false);
          return;
        }
        
        // Trim any whitespace
        decryptedEmail = decryptedEmail.trim();

        setEmail(decryptedEmail);

        // Step 4: Call the check-password-reset API with encrypted request
        const encryptedRequest = encryptData({ email: decryptedEmail });
        const response = await authAPI.checkPasswordReset(encryptedRequest);
        
        if (response.success && response.data) {
          setPasswordResetStatus(response.data.password_reset);
          
          if (response.data.password_reset) {
            // Password already reset, show modal
            setShowModal(true);
          }
          // If false, allow them to reset password (form will be shown)
        } else {
          setErrorMessage(response.message || 'Failed to verify reset link. Please try again.');
        }
      } catch (error: any) {
        console.error('Check password reset error:', error);
        const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Failed to verify reset link. Please try again.';
        setErrorMessage(errorMsg);
      } finally {
        setIsChecking(false);
      }
    };

    checkPasswordResetStatus();
  }, [token]);

  const handlePasswordSubmit = async (data: any) => {
    try {
      setIsLoading(true);
      setErrorMessage('');
      const response = await authAPI.forgetPassword(email, data.password);
      if (response.success) {
        toast.success('Password changed successfully! Please login with your new password.');
        navigate('/login');
      } else {
        setErrorMessage(response.message || 'Failed to change password. Please try again.');
      }
    } catch (error: any) {
      console.error('Reset password error:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Failed to change password. Please try again.';
      setErrorMessage(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  const renderPasswordForm = () => (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
      <div className="text-center mb-6">
        <div className="mx-auto h-16 w-16 bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl flex items-center justify-center shadow-lg mb-4">
          <Lock className="h-8 w-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Create New Password
        </h2>
        <p className="text-gray-600">
          Enter your new password below
        </p>
      </div>

      {/* Error Message Display */}
      {errorMessage && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm text-red-800">{errorMessage}</p>
            </div>
            <div className="ml-3 flex-shrink-0">
              <button
                type="button"
                onClick={() => setErrorMessage('')}
                className="text-red-400 hover:text-red-600 transition-colors"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      <form className="space-y-6" onSubmit={handleSubmit(handlePasswordSubmit)}>
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
            New Password
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Lock className="h-5 w-5 text-gray-400" />
            </div>
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              {...register('password', {
                required: 'Password is required',
                minLength: {
                  value: 8,
                  message: 'Password must be at least 8 characters'
                },
                pattern: {
                  value: /^(?=.*[A-Z])(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/,
                  message: 'Password must contain at least 8 characters, 1 capital letter, and 1 special character'
                }
              })}
              className="block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
              placeholder="Enter new password"
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              ) : (
                <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              )}
            </button>
          </div>
          {errors.password && (
            <p className="mt-2 text-sm text-red-600">{errors.password.message?.toString()}</p>
          )}
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
            Confirm New Password
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Lock className="h-5 w-5 text-gray-400" />
            </div>
            <input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              autoComplete="new-password"
              {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: (value) => value === password || 'Passwords do not match'
              })}
              className="block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
              placeholder="Confirm new password"
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? (
                <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              ) : (
                <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              )}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="mt-2 text-sm text-red-600">{errors.confirmPassword.message?.toString()}</p>
          )}
        </div>

        <div>
          <button
            type="submit"
            disabled={isLoading}
            className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            {isLoading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Resetting Password...
              </div>
            ) : (
              <div className="flex items-center">
                Reset Password
                <CheckCircle className="ml-2 h-4 w-4 group-hover:scale-110 transition-transform" />
              </div>
            )}
          </button>
        </div>
      </form>
    </div>
  );

  const renderLoadingState = () => (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
      <div className="text-center">
        <div className="mx-auto h-16 w-16 bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl flex items-center justify-center shadow-lg mb-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Verifying Reset Link
        </h2>
        <p className="text-gray-600">
          Please wait while we verify your password reset link...
        </p>
      </div>
    </div>
  );

  const renderErrorState = () => (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
      <div className="text-center mb-6">
        <div className="mx-auto h-16 w-16 bg-red-100 rounded-2xl flex items-center justify-center shadow-lg mb-4">
          <XCircle className="h-8 w-8 text-red-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Verification Failed
        </h2>
        <p className="text-gray-600 mb-6">
          {errorMessage || 'Unable to verify your reset link. Please request a new password reset.'}
        </p>
        <button
          onClick={() => navigate('/forgot-password')}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200 shadow-lg hover:shadow-xl"
        >
          Go to Forgot Password
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-50 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {isChecking ? (
          renderLoadingState()
        ) : errorMessage && passwordResetStatus === null ? (
          renderErrorState()
        ) : passwordResetStatus === false ? (
          renderPasswordForm()
        ) : null}

        {/* Modal for already reset password */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 max-w-md w-full">
              <div className="text-center mb-6">
                <div className="mx-auto h-16 w-16 bg-yellow-100 rounded-2xl flex items-center justify-center shadow-lg mb-4">
                  <XCircle className="h-8 w-8 text-yellow-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Password Already Reset
                </h2>
                <p className="text-gray-600">
                  You have already reset your password using this link. Please use your new password to login.
                </p>
              </div>
              <div className="mt-6">
                <button
                  onClick={handleGoToLogin}
                  className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  Go to Login Page
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;

